import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Chip,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useDispatch, useSelector } from "react-redux";
import ReactTimezoneSelect from "react-timezone-select";
import { fetchTemplates } from "../../redux/Slice/Templates/templateSlice";
import { buildTimezoneSelectStyles } from "./timezoneSelectStyles";

const DURATION_TYPE_OPTIONS = ["Minutes", "Hours", "Days"];

// Only these two categories are eligible for the "Send Template" trigger — an OTP/login
// template has no place starting a business workflow. Categories come back from Meta's
// Graph API as uppercase ("UTILITY"/"MARKETING"/"AUTHENTICATION").
const ELIGIBLE_TEMPLATE_CATEGORIES = ["UTILITY", "MARKETING"];

function extractBodyVariableIndexes(template) {
  const body = template?.components?.find((c) => (c.type || "").toUpperCase() === "BODY");
  if (!body?.text) return [];
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  const seen = new Set();
  let m;
  while ((m = re.exec(body.text)) !== null) seen.add(parseInt(m[1], 10));
  return Array.from(seen).sort((a, b) => a - b);
}

function extractQuickReplyButtons(template) {
  const buttonsComponent = template?.components?.find((c) => (c.type || "").toUpperCase() === "BUTTONS");
  const buttons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
  return buttons
    .filter((b) => (b.type || "").toUpperCase() === "QUICK_REPLY")
    .map((b) => ({ text: (b.text || "").trim() }))
    .filter((b) => b.text);
}

/**
 * Bespoke setup UI for the "Send Template" trigger, following the same pattern as
 * OfficeTimeFlowConfig.jsx: a self-contained form that hands a plain data object back to
 * ChooseTriggerDrawer.jsx via onSave, which owns all the Redux/branch-creation/backend-save
 * orchestration (mirroring the "Branch: Business Hours" action's branch-creation pattern in
 * ChooseActionDrawer.jsx).
 *
 * Three steps: (1) timezone/interval settings — same fields/labels as "Contact places an
 * order" so the existing generic expiry handling (workflow.expiry.js) applies unchanged;
 * (2) pick the template (search, Utility/Marketing only); (3) name the template's body
 * variables, shown only if it has any — these names become the payload keys later available
 * to the "From Template" source in an API Integration step.
 */
const SendTemplateTriggerConfig = ({ workflowId, existingTrigger, onSave, saving, isQrConnectedWorkspace = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { templates, status: templatesStatus } = useSelector((state) => state.template);

  // Editing an already-configured trigger (existingTrigger present) starts on the review step
  // with its stored template/variable-name info already in place, instead of forcing the whole
  // wizard to be redone from a blank timezone/interval/template pick every time.
  const [step, setStep] = useState(existingTrigger?.templateName ? 2 : 0);
  const [timezone, setTimezone] = useState(existingTrigger?.timezone || "");
  const [intervalValue, setIntervalValue] = useState(existingTrigger?.intervalValue ?? "");
  const [intervalType, setIntervalType] = useState(existingTrigger?.intervalType || "Minutes");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableNames, setVariableNames] = useState(() => {
    const initial = {};
    (existingTrigger?.variableMapping || []).forEach(({ index, name }) => {
      initial[index] = name;
    });
    return initial;
  });

  // Templates are 100% Meta-only (no WhatsappIntegrationModel row exists for a QR-connected
  // workspace to fetch them from). This component only mounts via an already-existing "Send
  // Template" trigger node being edited (new-trigger picking already hides it for QR) -- but that
  // edit-existing path is real, so guard the fetch here too rather than let it 400 pointlessly.
  useEffect(() => {
    if (templatesStatus === "idle" && !isQrConnectedWorkspace) {
      dispatch(fetchTemplates());
    }
  }, [templatesStatus, dispatch, isQrConnectedWorkspace]);

  const eligibleTemplates = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    return list.filter((t) => {
      const status = (t.status || "").toUpperCase();
      const category = (t.category || "").toUpperCase();
      return status === "APPROVED" && ELIGIBLE_TEMPLATE_CATEGORIES.includes(category);
    });
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return eligibleTemplates;
    return eligibleTemplates.filter((t) => (t.name || "").toLowerCase().includes(term));
  }, [eligibleTemplates, searchTerm]);

  // Editing only: once the template list loads, resolve existingTrigger's stored
  // name/id/language back to its real template object, so the review step's body-variable/
  // quick-reply-button lists reflect the template's current content (it may have changed since
  // the trigger was last saved) rather than only what was captured back then. variableNames
  // above is deliberately left untouched here — it's already seeded from
  // existingTrigger.variableMapping, and handlePickTemplate (a fresh pick, not this resolve)
  // is the only place that should reset it to defaults.
  useEffect(() => {
    if (!existingTrigger?.templateName || selectedTemplate || templatesStatus === "loading") return;
    const match = eligibleTemplates.find((t) =>
      (existingTrigger.templateId && t.id === existingTrigger.templateId) ||
      (t.name === existingTrigger.templateName && (!existingTrigger.language || t.language === existingTrigger.language))
    );
    if (match) setSelectedTemplate(match);
  }, [existingTrigger, eligibleTemplates, templatesStatus, selectedTemplate]);

  // Falls back to existingTrigger's own stored variableMapping/quickReplyButtons when no
  // selectedTemplate is resolved yet (still loading) or ever (template since renamed/deleted/
  // unapproved) — so editing an existing trigger still shows a correct, save-able review step
  // even when the live template can no longer be found in eligibleTemplates.
  const bodyVariableIndexes = useMemo(() => {
    if (selectedTemplate) return extractBodyVariableIndexes(selectedTemplate);
    if (existingTrigger?.variableMapping?.length) {
      return existingTrigger.variableMapping.map((v) => v.index).sort((a, b) => a - b);
    }
    return [];
  }, [selectedTemplate, existingTrigger]);
  const quickReplyButtons = useMemo(() => {
    if (selectedTemplate) return extractQuickReplyButtons(selectedTemplate);
    if (existingTrigger?.quickReplyButtons?.length) return existingTrigger.quickReplyButtons;
    return [];
  }, [selectedTemplate, existingTrigger]);

  // Same fallback reasoning as above, for the template identity fields handleFinish needs —
  // selectedTemplate stays null when editing until (if ever) the resolve effect above finds a
  // live match.
  const templateInfo = selectedTemplate
    ? { name: selectedTemplate.name, id: selectedTemplate.id, language: selectedTemplate.language, category: selectedTemplate.category }
    : existingTrigger?.templateName
      ? { name: existingTrigger.templateName, id: existingTrigger.templateId, language: existingTrigger.language, category: existingTrigger.templateCategory }
      : null;

  const handlePickTemplate = (template) => {
    setSelectedTemplate(template);
    const defaults = {};
    extractBodyVariableIndexes(template).forEach((idx) => {
      defaults[idx] = `variable_${idx}`;
    });
    setVariableNames(defaults);
    // Step 2 covers both cases: it shows the variable-naming section only when the template
    // actually has body variables, and always shows the branches preview + Save button.
    setStep(2);
  };

  const handleFinish = () => {
    if (!templateInfo) return;
    const variableMapping = bodyVariableIndexes.map((idx) => ({
      index: idx,
      name: (variableNames[idx] || `variable_${idx}`).trim().replace(/[^a-zA-Z0-9_]/g, "_") || `variable_${idx}`,
    }));
    onSave({
      timezone,
      intervalValue: intervalValue === "" ? 0 : parseInt(intervalValue, 10) || 0,
      intervalType,
      templateName: templateInfo.name,
      templateId: templateInfo.id,
      language: templateInfo.language,
      templateCategory: templateInfo.category,
      variableMapping,
      quickReplyButtons,
    });
  };

  const inputStyle = {
    width: "90%",
    marginTop: 4,
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${isDark ? "#444" : "#ccc"}`,
    background: isDark ? "#23272b" : "#fff",
    color: isDark ? "#fff" : "#222",
    fontSize: 15,
    marginBottom: 8,
  };

  if (step === 0) {
    return (
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}>Select Timezone</Typography>
        <Box sx={{ mt: 1, mb: 2, width: "90%" }}>
          <ReactTimezoneSelect
            value={timezone}
            onChange={(tz) => setTimezone(tz)}
            styles={buildTimezoneSelectStyles(isDark, { controlAndMenuWidth: "90%" })}
          />
        </Box>

        <Typography sx={{ fontWeight: 600, mb: 1, mt: 3, color: isDark ? "#fff" : "#222" }}>Select Time Interval</Typography>
        <Box sx={{ mb: 2 }}>
          <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>Select Interval Duration</label>
          <input
            type="number"
            min={0}
            value={intervalValue}
            onChange={(e) => setIntervalValue(e.target.value)}
            style={inputStyle}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>Select Duration Type</label>
          <FormControl size="small" sx={{ mt: 0.5, mb: 1, width: "90%", display: "block" }}>
            <Select value={intervalType} onChange={(e) => setIntervalType(e.target.value)}>
              {DURATION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={() => setStep(1)} sx={{ borderRadius: "20px", textTransform: "capitalize" }}>
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === 1) {
    return (
      <Box>
        <Button
          size="small"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={() => setStep(0)}
          sx={{ mb: 1, textTransform: "none", color: isDark ? "#bbb" : "#555" }}
        >
          Back
        </Button>
        <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}>Select a template</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates by name…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {templatesStatus === "loading" ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress size={24} /></Box>
        ) : filteredTemplates.length === 0 ? (
          <Typography fontSize={14} color={isDark ? "#bbb" : "text.secondary"}>
            No approved Utility or Marketing templates found.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 420, overflowY: "auto" }}>
            {filteredTemplates.map((t) => (
              <Box
                key={t.id || t.name}
                onClick={() => handlePickTemplate(t)}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                  cursor: "pointer",
                  "&:hover": { borderColor: theme.palette.primary.main, background: isDark ? "#2e3440" : "#f0f7ff" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography fontWeight={600} fontSize={14}>{t.name}</Typography>
                  <Chip size="small" label={t.category} />
                </Box>
                <Typography fontSize={12} color={isDark ? "#bbb" : "text.secondary"}>{t.language}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // step === 2: name variables (if any) + review branches, then finish
  return (
    <Box>
      <Button
        size="small"
        startIcon={<ArrowBackIosNewIcon fontSize="small" />}
        onClick={() => setStep(1)}
        sx={{ mb: 1, textTransform: "none", color: isDark ? "#bbb" : "#555" }}
      >
        Back
      </Button>
      <Typography sx={{ fontWeight: 600, mb: 0.5, color: isDark ? "#fff" : "#222" }}>
        Selected: {templateInfo?.name || "No template selected"}
      </Typography>

      {bodyVariableIndexes.length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography fontSize={13} fontWeight={600} sx={{ mb: 1, color: isDark ? "#bbb" : "#555" }}>
            Name this template's variables
          </Typography>
          <Typography fontSize={12} color={isDark ? "#999" : "text.secondary"} sx={{ mb: 1 }}>
            These names will be available in later Integration steps as the payload key for the
            value filled in when this template is sent.
          </Typography>
          {bodyVariableIndexes.map((idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography fontSize={13} sx={{ minWidth: 40, color: isDark ? "#bbb" : "#555" }}>{`{{${idx}}}`}</Typography>
              <TextField
                size="small"
                fullWidth
                value={variableNames[idx] || ""}
                onChange={(e) => setVariableNames((v) => ({ ...v, [idx]: e.target.value }))}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography fontSize={13} fontWeight={600} sx={{ mb: 1, color: isDark ? "#bbb" : "#555" }}>
          {quickReplyButtons.length > 0
            ? `This will create ${quickReplyButtons.length} branch${quickReplyButtons.length > 1 ? "es" : ""}:`
            : "This template has no quick-reply buttons — no branches will be created."}
        </Typography>
        {quickReplyButtons.map((b) => (
          <Chip key={b.text} label={b.text} size="small" sx={{ mr: 1, mb: 1 }} />
        ))}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleFinish}
          disabled={!!saving || !templateInfo}
          sx={{ borderRadius: "20px", textTransform: "capitalize" }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default SendTemplateTriggerConfig;
